// Style master code and SKU item number [MB D6; 03 BR-DM-003; 02 BR-NUM-004]. Widths are frozen
// constants (settings `itemcode.width.*` expose them read-only; edit → 409 FROZEN_SETTING).
import { AppError } from './errors.ts';

export const ITEM_CODE_WIDTHS = Object.freeze({
  style: 8,
  group: 6,
  season: 3,
  colour: 3,
  size: 4,
} as const);

export type ItemCodePart = keyof typeof ITEM_CODE_WIDTHS;

export const STYLE_MASTER_CODE_LENGTH =
  ITEM_CODE_WIDTHS.style + ITEM_CODE_WIDTHS.group + ITEM_CODE_WIDTHS.season; // 17
export const ITEM_NO_LENGTH =
  STYLE_MASTER_CODE_LENGTH + ITEM_CODE_WIDTHS.colour + ITEM_CODE_WIDTHS.size; // 24

const PART_RE = /^[A-Z0-9][A-Z0-9 ./-]*$/;

function pad(part: ItemCodePart, raw: string): string {
  const value = raw.trim().toUpperCase();
  const width = ITEM_CODE_WIDTHS[part];
  if (value.length === 0 || value.length > width || !PART_RE.test(value)) {
    throw new AppError('VALIDATION_FAILED', {
      fields: [
        {
          path: part,
          code: 'invalid_item_code_part',
          messageKey: 'errors.itemCode.invalidPart',
          params: { part, width },
        },
      ],
    });
  }
  return value.padEnd(width, ' ');
}

/** rpad(style,8) + rpad(group,6) + rpad(season,3) = 17 chars. */
export function composeStyleMasterCode(style: string, group: string, season: string): string {
  return pad('style', style) + pad('group', group) + pad('season', season);
}

/** style master (17) + rpad(colour,3) + rpad(size,4) = 24 chars. */
export function composeItemNo(styleMasterCode: string, colour: string, size: string): string {
  if (styleMasterCode.length !== STYLE_MASTER_CODE_LENGTH) {
    throw new AppError('VALIDATION_FAILED', {
      fields: [
        {
          path: 'styleMasterCode',
          code: 'invalid_length',
          messageKey: 'errors.itemCode.masterLength',
        },
      ],
    });
  }
  return styleMasterCode + pad('colour', colour) + pad('size', size);
}

export interface ItemNoParts {
  readonly style: string;
  readonly group: string;
  readonly season: string;
  readonly colour: string;
  readonly size: string;
}

/** Splits a 24-char item number into trimmed parts (inverse of compose; used by trigger checks). */
export function parseItemNo(itemNo: string): ItemNoParts {
  if (itemNo.length !== ITEM_NO_LENGTH) {
    throw new AppError('VALIDATION_FAILED', {
      fields: [
        { path: 'itemNo', code: 'invalid_length', messageKey: 'errors.itemCode.itemLength' },
      ],
    });
  }
  let at = 0;
  const take = (part: ItemCodePart): string => {
    const s = itemNo.slice(at, at + ITEM_CODE_WIDTHS[part]).trimEnd();
    at += ITEM_CODE_WIDTHS[part];
    return s;
  };
  return {
    style: take('style'),
    group: take('group'),
    season: take('season'),
    colour: take('colour'),
    size: take('size'),
  };
}
